import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import UserAvatar from "../components/UserAvatar";
import PostCard from "../components/PostCard";
import { getModelName } from "../lib/aiModels";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  Home, User, Bot, Bell, CreditCard, Settings, Plus,
  Sparkles, Send, Loader2, ChevronDown, Search, Lock, Palette, Heart, Trophy, Flame, ShoppingBag, Zap, BarChart3
} from "lucide-react";
import api from "../lib/api";
import AIStudioModal from "../components/AIStudioModal";

const MOODS = [
  { id: "default", label: "Everything", emoji: "🌐", activeColor: "bg-muted text-foreground border-white/25", inactiveColor: "bg-muted/40 text-muted-foreground border-white/10" },
  { id: "uplifting", label: "Uplifting", emoji: "😊", activeColor: "bg-amber-500/15 text-amber-300 border-amber-500/40", inactiveColor: "bg-amber-500/10 text-amber-300 border-amber-500/30" },
  { id: "creative", label: "Creative", emoji: "🎨", activeColor: "bg-purple-500/15 text-purple-300 border-purple-500/40", inactiveColor: "bg-purple-500/10 text-purple-300 border-purple-500/30" },
  { id: "social", label: "Social", emoji: "👥", activeColor: "bg-green-500/15 text-green-300 border-green-500/40", inactiveColor: "bg-green-500/10 text-green-300 border-green-500/30" },
  { id: "focused", label: "Focused", emoji: "🎯", activeColor: "bg-blue-500/15 text-blue-300 border-blue-500/40", inactiveColor: "bg-blue-500/10 text-blue-300 border-blue-500/30" },
  { id: "chill", label: "Chill", emoji: "😌", activeColor: "bg-teal-500/15 text-teal-300 border-teal-500/40", inactiveColor: "bg-teal-500/10 text-teal-300 border-teal-500/30" },
];

export default function FeedPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingFeed, setLoadingFeed] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Create post
  const [showCreate, setShowCreate] = useState(false);
  const [postText, setPostText] = useState("");
  const [postVisibility, setPostVisibility] = useState("public");
  const [posting, setPosting] = useState(false);

  // Right sidebar
  const [suggestions, setSuggestions] = useState([]);
  const [assistants, setAssistants] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // AI auto-post
  const [aiPostModal, setAiPostModal] = useState(null); // { assistant, content, loading }
  const [aiPostText, setAiPostText] = useState("");

  // Mood & AI Studio
  const [currentMood, setCurrentMood] = useState("default");
  const [showStudio, setShowStudio] = useState(false);

  // Challenge & Streak
  const [challenge, setChallenge] = useState(null);
  const [streak, setStreak] = useState(null);

  const [consentBannerDismissed, setConsentBannerDismissed] = useState(() => localStorage.getItem("privacy_consent_seen") === "true");

  const dismissConsentBanner = () => {
    localStorage.setItem("privacy_consent_seen", "true");
    setConsentBannerDismissed(true);
  };

  const observerRef = useRef(null);
  const sentinelRef = useRef(null);

  // Load profile
  useEffect(() => {
    api.get("/profile/me").then((r) => {
      if (!r.data.has_profile) navigate("/profile/setup");
      else setProfile(r.data);
    }).catch(() => navigate("/profile/setup"));
  }, [navigate]);

  // Load feed
  const loadFeed = useCallback(async (p = 1, append = false) => {
    if (p === 1) setLoadingFeed(true);
    else setLoadingMore(true);
    try {
      const r = await api.get(`/posts/feed?page=${p}&limit=10&mood=${currentMood}`);
      if (append) setPosts((prev) => [...prev, ...r.data.posts]);
      else setPosts(r.data.posts);
      setHasMore(r.data.has_more);
      setPage(p);
    } catch { toast.error("Failed to load feed"); }
    finally { setLoadingFeed(false); setLoadingMore(false); }
  }, [currentMood]);

  useEffect(() => { loadFeed(1); }, [loadFeed]);

  const handleMoodChange = async (mood) => {
    setCurrentMood(mood);
    try { await api.put("/feed/mood", { mood }); } catch { /* ignore */ }
  };

  // Load sidebar data
  useEffect(() => {
    api.get("/suggestions/who-to-follow").then((r) => setSuggestions(r.data)).catch(() => {});
    api.get("/assistants").then((r) => setAssistants(r.data.slice(0, 3))).catch(() => {});
    api.get("/notifications/unread-count").then((r) => setUnreadCount(r.data.count)).catch(() => {});
    api.get("/challenges/today").then((r) => setChallenge(r.data)).catch(() => {});
    api.get("/streaks/me").then((r) => setStreak(r.data)).catch(() => {});
  }, []);

  // Infinite scroll
  useEffect(() => {
    if (!sentinelRef.current) return;
    observerRef.current = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && hasMore && !loadingMore && !loadingFeed) {
        loadFeed(page + 1, true);
      }
    }, { threshold: 0.1 });
    observerRef.current.observe(sentinelRef.current);
    return () => observerRef.current?.disconnect();
  }, [hasMore, loadingMore, loadingFeed, page, loadFeed]);

  // Create post
  const handleCreatePost = async () => {
    if (!postText.trim() || posting) return;
    setPosting(true);
    try {
      await api.post("/posts", { content_text: postText.trim(), visibility: postVisibility, post_type: "text" });
      toast.success("Post published!");
      setPostText("");
      setShowCreate(false);
      loadFeed(1);
    } catch { toast.error("Failed to create post"); }
    finally { setPosting(false); }
  };

  // Follow suggestion
  const handleFollow = async (username) => {
    try {
      await api.post(`/follow/${username}`);
      setSuggestions((prev) => prev.filter((s) => s.username !== username));
      toast.success(`Followed @${username}`);
    } catch { toast.error("Failed to follow"); }
  };

  // AI auto-post
  const handleGenerateAIPost = async (assistant) => {
    setAiPostModal({ assistant, content: "", loading: true });
    setAiPostText("");
    try {
      const r = await api.post(`/ai-posts/generate/${assistant.id}`);
      setAiPostText(r.data.preview_content);
      setAiPostModal({ assistant, content: r.data.preview_content, loading: false });
    } catch {
      toast.error("Failed to generate post");
      setAiPostModal(null);
    }
  };

  const handlePublishAIPost = async () => {
    if (!aiPostModal || !aiPostText.trim()) return;
    try {
      await api.post(`/ai-posts/publish/${aiPostModal.assistant.id}`, { content_text: aiPostText.trim() });
      toast.success(`Posted by ${aiPostModal.assistant.name}!`);
      setAiPostModal(null);
      loadFeed(1);
    } catch { toast.error("Failed to publish"); }
  };

  const handleStudioPublish = async (text) => {
    try {
      await api.post("/posts", { content_text: text, visibility: "public", post_type: "ai_generated" });
      toast.success("AI post published!");
      loadFeed(1);
    } catch { toast.error("Failed to publish"); }
  };

  if (!profile) return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="w-6 h-6 text-cyan-400 animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen" data-testid="feed-page">
      {/* Mobile Top Bar */}
      <div className="lg:hidden sticky top-0 z-40 nav-glass px-4 h-12 flex items-center justify-between">
        <Link to="/feed" className="flex items-center gap-2 no-underline">
          <div className="w-6 h-6 rounded-md bg-gradient-to-br from-cyan-400 to-violet-500 flex items-center justify-center">
            <Sparkles className="w-3 h-3 text-white" />
          </div>
          <span className="font-outfit text-base font-semibold text-foreground">App-Mi.ai</span>
        </Link>
        <Link to="/notifications" className="relative" data-testid="mobile-notif-btn">
          <Bell className="w-5 h-5 text-muted-foreground" />
          {unreadCount > 0 && <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-[9px] text-white flex items-center justify-center font-medium">{unreadCount > 9 ? "9+" : unreadCount}</span>}
        </Link>
      </div>

      <div className="max-w-7xl mx-auto flex">
        {/* ─── Left Sidebar (Desktop) ─── */}
        <aside className="hidden lg:block w-[250px] flex-shrink-0 sticky top-0 h-screen overflow-y-auto border-r border-white/[0.07] bg-[#0a0d20]/60 backdrop-blur-xl bg-sidebar-left p-5" data-testid="left-sidebar">
          {/* User Info */}
          <div className="flex items-center gap-3 mb-3 pb-3 border-b border-white/10">
            <UserAvatar name={profile.display_name} username={profile.username} avatarUrl={profile.avatar_url} size="md" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-white truncate">{profile.display_name}</p>
              <p className="text-xs text-cyan-300 truncate">@{profile.username}</p>
            </div>
          </div>

          {/* Streak Counter */}
          {streak && (
            <div className="flex items-center gap-2 mb-5 pb-4 border-b border-white/10 bg-white/5 rounded-lg px-3 py-2" data-testid="sidebar-streak">
              <span className="text-lg">{streak.current_streak > 0 ? "🔥" : "💤"}</span>
              <div>
                <p className="text-xs font-semibold text-white">{streak.current_streak} day streak</p>
                {!streak.posted_today && <p className="text-[10px] text-orange-300">Post to keep it alive!</p>}
              </div>
            </div>
          )}

          {/* Nav Links */}
          <nav className="space-y-1">
            {[
              { icon: Home, label: "Feed", to: "/feed", active: true },
              { icon: Send, label: "Publish", to: "/publish" },
              { icon: Search, label: "Explore", to: "/explore" },
              { icon: User, label: "My Profile", to: `/profile/${profile.username}` },
              { icon: Bot, label: "My Companions", to: "/dashboard" },
              { icon: Bell, label: "Notifications", to: "/notifications", badge: unreadCount },
              { icon: CreditCard, label: "Subscription", to: "/subscription" },
              { icon: Heart, label: "Relationships", to: "/relationships" },
              { icon: Trophy, label: "Achievements", to: "/achievements" },
              { icon: Trophy, label: "Leaderboard", to: "/leaderboard" },
              { icon: ShoppingBag, label: "Marketplace", to: "/marketplace" },
              { icon: Zap, label: "Integrations", to: "/integrations" },
              { icon: BarChart3, label: "Analytics", to: "/analytics" },
              { icon: Settings, label: "Privacy", to: "/settings/privacy" },
              { icon: Palette, label: "AI Studio", action: () => setShowStudio(true) },
            ].map((item) => (
              item.action ? (
                <button
                  key={item.label}
                  onClick={item.action}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-cyan-200 hover:bg-white/10 hover:text-white transition-colors w-full bg-transparent border-0 cursor-pointer text-left"
                  data-testid={`sidebar-${item.label.toLowerCase().replace(/ /g, "-")}`}
                >
                  <item.icon className="w-5 h-5" /> {item.label}
                </button>
              ) : (
              <Link
                key={item.to}
                to={item.to}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium no-underline transition-colors ${
                  item.active ? "bg-white/15 text-white" : "text-cyan-200 hover:bg-white/10 hover:text-white"
                }`}
                data-testid={`sidebar-${item.label.toLowerCase().replace(/ /g, "-")}`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
                {item.badge > 0 && <span className="ml-auto w-5 h-5 bg-red-500 rounded-full text-[10px] text-white flex items-center justify-center font-medium">{item.badge > 9 ? "9+" : item.badge}</span>}
              </Link>
              )
            ))}
          </nav>
        </aside>

        {/* ─── Center Feed ─── */}
        <main className="flex-1 min-w-0 max-w-[600px] mx-auto px-4 py-4 pb-20 lg:pb-4" data-testid="feed-center">
          {/* Consent Banner */}
          {!consentBannerDismissed && (
            <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-xl px-4 py-3 mb-4 flex items-start gap-3 animate-fade-in" data-testid="consent-banner">
              <Lock className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-cyan-200">App-Mi.ai respects your privacy. Your conversations are encrypted and your AI never shares your data. <Link to="/settings/privacy" className="text-cyan-400 font-medium no-underline hover:underline">Manage preferences</Link></p>
              </div>
              <button onClick={dismissConsentBanner} className="text-xs text-cyan-400 font-medium bg-transparent border-0 cursor-pointer flex-shrink-0 hover:text-cyan-300" data-testid="dismiss-banner">Got it</button>
            </div>
          )}

          {/* Mood Bar */}
          <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-1 -mx-1 px-1 bg-gradient-to-r from-cyan-500/15 via-purple-500/15 to-pink-500/15 py-2 rounded-xl" data-testid="mood-bar">
            {MOODS.map((m) => (
              <button
                key={m.id}
                onClick={() => handleMoodChange(m.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap border cursor-pointer transition-all ${
                  currentMood === m.id
                    ? `${m.activeColor} shadow-sm`
                    : m.inactiveColor
                }`}
                data-testid={`mood-${m.id}`}
              >
                <span>{m.emoji}</span> {m.label}
              </button>
            ))}
          </div>
          {currentMood !== "default" && (
            <p className="text-[10px] text-muted-foreground/70 mb-3 -mt-2">Your AI curates your feed based on your mood</p>
          )}

          {/* Daily Challenge */}
          {challenge && !challenge.completed && (
            <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-xl px-4 py-3 mb-4 animate-fade-in" data-testid="daily-challenge">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <span className="text-lg">✨</span>
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-foreground">Today's Challenge</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{challenge.challenge_text}</p>
                    <p className="text-[10px] text-muted-foreground/70 mt-0.5">{challenge.completions_today} people completed today</p>
                  </div>
                </div>
                <button onClick={() => { setShowCreate(true); setPostText(challenge.challenge_text + "\n\n"); }} className="text-xs font-medium text-white bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-full border-0 cursor-pointer transition-colors flex-shrink-0 ml-3" data-testid="accept-challenge-btn">Accept</button>
              </div>
            </div>
          )}

          {/* Create Post Card */}
          <div className="bg-card rounded-xl border border-cyan-500/20 shadow-sm mb-4 overflow-hidden border-t-4 border-t-indigo-400" data-testid="create-post-card">
            {!showCreate ? (
              <div className="px-4 py-3 flex items-center gap-3 cursor-pointer" onClick={() => setShowCreate(true)}>
                <UserAvatar name={profile.display_name} username={profile.username} avatarUrl={profile.avatar_url} size="md" />
                <div className="flex-1 px-4 py-2.5 bg-muted/40 rounded-full text-sm text-muted-foreground/70 hover:bg-white/10 transition-colors">
                  What's on your mind, {profile.display_name?.split(" ")[0]}?
                </div>
              </div>
            ) : (
              <div className="p-4">
                <div className="flex items-start gap-3 mb-3">
                  <UserAvatar name={profile.display_name} username={profile.username} avatarUrl={profile.avatar_url} size="md" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-foreground">{profile.display_name}</p>
                    <select value={postVisibility} onChange={(e) => setPostVisibility(e.target.value)} className="text-[11px] text-muted-foreground bg-muted/40 border border-border rounded px-1.5 py-0.5 mt-0.5 cursor-pointer" data-testid="visibility-select">
                      <option value="public">Public</option>
                      <option value="followers_only">Followers Only</option>
                    </select>
                  </div>
                </div>
                <textarea
                  data-testid="create-post-input"
                  value={postText}
                  onChange={(e) => setPostText(e.target.value)}
                  placeholder="What's on your mind?"
                  rows={3}
                  maxLength={5000}
                  className="w-full bg-transparent text-sm text-foreground placeholder:text-muted-foreground/70 resize-none focus:outline-none mb-3"
                  autoFocus
                />
                <div className="flex items-center justify-between border-t border-border/60 pt-3">
                  <div className="flex gap-1">
                    {/* Photo upload from the inline composer is not yet
                        implemented end-to-end (no upload pipeline); use AI
                        Studio for image-based posts. */}
                    <button onClick={() => setShowStudio(true)} className="p-2 rounded-lg text-muted-foreground/70 hover:text-violet-500 hover:bg-violet-500/10 bg-transparent border-0 cursor-pointer transition-colors" title="AI Studio" data-testid="create-ai-studio-btn">
                      <Sparkles className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => { setShowCreate(false); setPostText(""); }} className="px-3 py-1.5 text-xs text-muted-foreground bg-transparent border-0 cursor-pointer hover:text-foreground/80">Cancel</button>
                    <Button
                      data-testid="publish-post-btn"
                      onClick={handleCreatePost}
                      disabled={!postText.trim() || posting}
                      className="bg-gradient-to-r from-cyan-400 to-violet-500 text-white text-xs px-4 h-8 rounded-lg disabled:opacity-40 press-down"
                    >
                      {posting ? <Loader2 className="w-3 h-3 animate-spin" /> : "Post"}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Feed Posts */}
          {loadingFeed ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-card rounded-xl border border-border/60 p-4">
                  <div className="flex gap-3 mb-3"><div className="w-10 h-10 rounded-full skeleton" /><div className="flex-1"><div className="h-4 w-32 skeleton mb-1" /><div className="h-3 w-20 skeleton" /></div></div>
                  <div className="h-4 w-full skeleton mb-2" /><div className="h-4 w-3/4 skeleton mb-2" /><div className="h-4 w-1/2 skeleton" />
                </div>
              ))}
            </div>
          ) : posts.length === 0 ? (
            <div className="bg-card rounded-xl border border-border/60 p-12 text-center" data-testid="feed-empty">
              <Sparkles className="w-8 h-8 text-cyan-300 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground mb-1">Your feed is empty</p>
              <p className="text-xs text-muted-foreground/70">Follow people or create your first post!</p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="feed-posts">
              {posts.map((post) => (
                <PostCard key={post.id} post={post} />
              ))}
              {/* Infinite scroll sentinel */}
              <div ref={sentinelRef} className="h-4" />
              {loadingMore && (
                <div className="flex justify-center py-4">
                  <Loader2 className="w-5 h-5 text-cyan-400 animate-spin" />
                </div>
              )}
              {!hasMore && posts.length > 5 && (
                <p className="text-center text-xs text-muted-foreground/70 py-4">You've seen all posts</p>
              )}
            </div>
          )}
        </main>

        {/* ─── Right Sidebar (Desktop) ─── */}
        <aside className="hidden xl:block w-[300px] flex-shrink-0 sticky top-0 h-screen overflow-y-auto p-5 border-l border-white/[0.07] bg-[#0a0d20]/60 backdrop-blur-xl bg-sidebar-right" data-testid="right-sidebar">
          {/* Who to Follow */}
          {suggestions.length > 0 && (
            <div className="mb-6">
              <h3 className="text-xs uppercase tracking-wider text-cyan-400 font-semibold mb-3">Who to Follow</h3>
              <div className="space-y-3">
                {suggestions.map((s) => (
                  <div key={s.username} className="flex items-center gap-3 bg-white/5 rounded-lg p-2" data-testid={`suggestion-${s.username}`}>
                    <UserAvatar name={s.display_name} username={s.username} avatarUrl={s.avatar_url} size="sm" />
                    <div className="flex-1 min-w-0">
                      <Link to={`/profile/${s.username}`} className="text-xs font-semibold text-white no-underline hover:text-cyan-300 block truncate">{s.display_name}</Link>
                      <p className="text-[10px] text-cyan-400 truncate">@{s.username}</p>
                    </div>
                    <button onClick={() => handleFollow(s.username)} className="text-[11px] font-medium text-white bg-indigo-500 hover:bg-indigo-400 px-3 py-1 rounded-full border-0 cursor-pointer transition-colors" data-testid={`follow-btn-${s.username}`}>Follow</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Your AIs */}
          {assistants.length > 0 && (
            <div className="mb-6">
              <h3 className="text-xs uppercase tracking-wider text-cyan-400 font-semibold mb-3">Your AI Companions</h3>
              <div className="space-y-2">
                {assistants.map((a) => (
                  <div key={a.id} className="flex items-center gap-3 bg-white/5 rounded-lg border border-white/10 p-3" data-testid={`ai-quick-${a.slug}`}>
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-400 to-violet-500 flex items-center justify-center text-white text-xs font-medium">{a.name?.charAt(0)}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-white truncate max-w-[100px]" title={a.name}>{a.name}</p>
                      <p className="text-[10px] text-cyan-400">{getModelName(a.ai_model)}</p>
                    </div>
                    <Link to={`/chat/${a.slug}`} className="text-[10px] font-medium text-cyan-300 no-underline hover:text-white">Chat</Link>
                    <button onClick={() => handleGenerateAIPost(a)} className="text-[10px] font-medium text-purple-300 bg-purple-500/20 hover:bg-purple-500/30 px-2 py-0.5 rounded border-0 cursor-pointer transition-colors" data-testid={`gen-post-${a.slug}`}>Post</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Trending */}
          <div className="mb-6">
            <h3 className="text-xs uppercase tracking-wider text-cyan-400 font-semibold mb-3">Trending</h3>
            <div className="space-y-2">
              {["#AICompanions", "#ProductivityHack", "#TechTips", "#FinanceTips", "#WritingCommunity"].map((tag) => (
                <div key={tag} className="text-xs text-cyan-300 font-medium cursor-pointer hover:text-white">{tag}</div>
              ))}
            </div>
          </div>

          {/* Search the Web */}
          <div>
            <h3 className="text-xs uppercase tracking-wider text-cyan-400 font-semibold mb-3">Search the Web</h3>
            <form onSubmit={(e) => { e.preventDefault(); navigate(`/explore?mode=web`); }} className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-cyan-400" />
              <input placeholder="Ask AI anything..." onClick={() => navigate("/explore")} readOnly className="w-full pl-8 pr-3 py-2 bg-white/10 border border-white/10 rounded-lg text-xs text-white placeholder:text-cyan-400 cursor-pointer hover:bg-white/15 transition-colors" data-testid="sidebar-web-search" />
            </form>
          </div>
        </aside>
      </div>

      {/* ─── Mobile Bottom Tab Bar ─── */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-card border-t border-border/60 h-14 flex items-center justify-around px-2" data-testid="bottom-tabs">
        {[
          { icon: Home, label: "Home", to: "/feed", active: true },
          { icon: Search, label: "Explore", to: "/explore" },
          { icon: Plus, label: "Create", action: () => setShowCreate(true), isCreate: true },
          { icon: Bell, label: "Notifs", to: "/notifications", badge: unreadCount },
          { icon: User, label: "Profile", to: profile ? `/profile/${profile.username}` : "/feed" },
        ].map((tab) => (
          tab.action ? (
            <button key={tab.label} onClick={tab.action} className="flex flex-col items-center gap-0.5 bg-transparent border-0 cursor-pointer" data-testid={`tab-${tab.label.toLowerCase()}`}>
              <div className="w-9 h-9 rounded-full bg-gradient-to-r from-cyan-400 to-violet-500 flex items-center justify-center text-white shadow-md -mt-3">
                <tab.icon className="w-5 h-5" />
              </div>
            </button>
          ) : (
            <Link key={tab.label} to={tab.to} className={`flex flex-col items-center gap-0.5 no-underline relative ${tab.active ? "text-cyan-400" : "text-muted-foreground/70"}`} data-testid={`tab-${tab.label.toLowerCase()}`}>
              <tab.icon className="w-5 h-5" />
              <span className="text-[10px]">{tab.label}</span>
              {tab.badge > 0 && <span className="absolute -top-0.5 right-0 w-3.5 h-3.5 bg-red-500 rounded-full text-[8px] text-white flex items-center justify-center">{tab.badge > 9 ? "+" : tab.badge}</span>}
            </Link>
          )
        ))}
      </div>

      {/* AI Auto-Post Modal */}
      {aiPostModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => !aiPostModal.loading && setAiPostModal(null)} data-testid="ai-post-modal">
          <div className="bg-card rounded-2xl shadow-xl max-w-lg w-full p-6 animate-fade-in-up" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-400 to-violet-500 flex items-center justify-center text-white font-medium">{aiPostModal.assistant.name?.charAt(0)}</div>
              <div>
                <p className="text-sm font-semibold text-foreground">{aiPostModal.assistant.name}</p>
                <p className="text-[11px] text-muted-foreground">AI-generated post</p>
              </div>
            </div>
            {aiPostModal.loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 text-cyan-400 animate-spin" />
                <span className="text-sm text-muted-foreground ml-2">Generating post...</span>
              </div>
            ) : (
              <>
                <textarea
                  data-testid="ai-post-textarea"
                  value={aiPostText}
                  onChange={(e) => setAiPostText(e.target.value)}
                  rows={4}
                  className="w-full bg-muted/40 border border-border rounded-lg px-4 py-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-cyan-400/25 focus:border-cyan-400 resize-none mb-4"
                />
                <div className="flex items-center justify-between">
                  <button onClick={() => handleGenerateAIPost(aiPostModal.assistant)} className="text-xs text-cyan-400 font-medium bg-transparent border-0 cursor-pointer hover:text-cyan-300" data-testid="ai-post-regenerate">Regenerate</button>
                  <div className="flex gap-2">
                    <button onClick={() => setAiPostModal(null)} className="px-4 py-2 text-xs text-muted-foreground bg-transparent border-0 cursor-pointer hover:text-foreground/80">Cancel</button>
                    <Button onClick={handlePublishAIPost} disabled={!aiPostText.trim()} className="bg-gradient-to-r from-cyan-400 to-violet-500 text-white text-xs px-4 h-8 rounded-lg disabled:opacity-40 press-down" data-testid="ai-post-publish">
                      Publish to Feed
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* AI Studio Modal */}
      <AIStudioModal open={showStudio} onClose={() => setShowStudio(false)} onPublish={handleStudioPublish} />
    </div>
  );
}
